import { Result } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DataTable } from '@kolab/dms-base/components/ui'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { listPoolAvailability } from '../../api/report.api'
import type { PoolAvailability } from '../../api/types'
import { StatusBadge } from '../../components/common/StatusBadge'
import { useCursorList } from '../../hooks/useCursorList'
import { CursorListStateView } from '../../components/common/QueryStateView'
import { P, usePermissionStore } from '../../rbac'


export default function PoolAvailabilityReport() {
  const { hasPermission } = usePermissionStore()
  const canView = hasPermission(P.reports.pool_availability.view)

  if (!canView) {
    return (
      <PageLayout>
        <Result
          status="403"
          title="Access denied"
          subTitle="You do not have permission to view the pool availability report."
        />
      </PageLayout>
    )
  }

  return <PoolAvailabilityTable />
}

function PoolAvailabilityTable() {
  const { items, isLoading, isError, error, refetch, isFetchingNextPage, hasNextPage, fetchNextPage } = useCursorList<PoolAvailability>({
    queryKey: ['reports', 'pools', 'infinite', { pageSize: 20 }],
    queryFn: (params) => listPoolAvailability(params),
    pageSize: 20,
  })

  const columns: ColumnsType<PoolAvailability> = [
    { title: 'License key', dataIndex: 'licenseKey', key: 'licenseKey' },
    {
      title: 'Distributor',
      dataIndex: 'distributorName',
      key: 'distributorName',
    },
    { title: 'Type', dataIndex: 'licenseType', key: 'licenseType' },
    { title: 'Total', dataIndex: 'totalDeviceLimit', key: 'totalDeviceLimit', align: 'right' as const },
    { title: 'Allocated', dataIndex: 'allocatedDeviceLimit', key: 'allocatedDeviceLimit', align: 'right' as const },
    { title: 'Available', dataIndex: 'availableDeviceLimit', key: 'availableDeviceLimit', align: 'right' as const },
    { title: 'Expiry', dataIndex: 'expiryDate', key: 'expiryDate' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (s: string) => <StatusBadge status={s} /> },
  ]

  return (
    <PageLayout>
      <CursorListStateView isLoading={isLoading} isError={isError} error={error} refetch={refetch}>
        <DataTable<PoolAvailability>
          rowKey="distributorLicenseId"
          columns={columns}
          dataSource={items}
          pagination={false}
          infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        />
      </CursorListStateView>
    </PageLayout>
  )
}
